import React from 'react'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { Link } from 'react-router-dom'

import { formatDate } from '../../utils/formatDate'

import {
  getCaseStatusMeta,
  getReportDeepLink,
  getReportIdentifier,
  getReportStatusBadgeMeta,
  getReportStatusDisplay,
} from './caseStatusHelpers'

/** @typedef { import('../../services/types/CaseSearchResultItem').CaseSearchResultItem } CaseSearchResultItem */

export function ReportStatusBadge({ rowData, settings, showEmptyLabel = false }) {
  if (!rowData?.reportStatus) {
    return <span className="report-status-empty text-muted">{showEmptyLabel ? 'No reports' : <>&mdash;</>}</span>
  }

  const identifier = getReportIdentifier(rowData)
  const displayLabel = getReportStatusDisplay(rowData.reportStatus, settings)
  const badgeMeta = getReportStatusBadgeMeta(rowData.reportStatus)
  const deepLink = getReportDeepLink(rowData)
  const extraOpenCount = (rowData.openReportCount || 0) > 1 ? rowData.openReportCount - 1 : 0

  return (
    <div className="report-status-cluster">
      {identifier && (
        <span className="report-status-identifier" title={`${identifier} (${displayLabel})`}>
          {identifier}
        </span>
      )}
      <div className="d-inline-flex align-items-center">
        <Link
          to={deepLink}
          className={`report-status-pill ${badgeMeta.badgeClass}`}
          title={`Open ${identifier || 'Report'} — ${displayLabel}`}
        >
          <span className="report-status-pill__dot" />
          <span className="report-status-pill__text">{displayLabel}</span>
          <span className="report-status-pill__arrow" aria-hidden="true">
            &#8599;
          </span>
        </Link>
        {extraOpenCount > 0 && (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`open-rep-count-${rowData.caseNumber}`}>
                {rowData.openReportCount} open reports on this case
              </Tooltip>
            }
          >
            <span className="report-status-extra-count ml-1">+{extraOpenCount}</span>
          </OverlayTrigger>
        )}
      </div>
    </div>
  )
}

/**
 * @param {{ rowData: CaseSearchResultItem, onMemberClick?: (name: string) => void, settings?: any }} props
 */
export function CaseResultRow(props) {
  const { rowData, onMemberClick, settings } = props

  const linkHref = `/cases/${rowData.caseNumber}?returnTo=/cases`
  const caseStatusMeta = getCaseStatusMeta(rowData.status)

  const renderMembers = () => {
    if (!rowData.members || rowData.members.length === 0) return ''
    return rowData.members.map((member, idx) => (
      <React.Fragment key={member.id || idx}>
        {idx > 0 && ', '}
        <a
          href="#!"
          className="member-filter-link"
          onClick={e => {
            e.preventDefault()
            onMemberClick?.(member.fullName)
          }}
        >
          {member.fullName}
        </a>
      </React.Fragment>
    ))
  }

  return (
    <tr>
      <th scope="row">
        <Link to={linkHref}>
          <div className="column-wrapper font-weight-bold">{rowData.clientFullName}</div>
        </Link>
      </th>
      <td className="d-none d-md-table-cell">
        <div className="column-wrapper">{renderMembers()}</div>
      </td>
      <td className="d-none d-xl-table-cell">
        <div className="column-wrapper">{rowData.company}</div>
      </td>
      <td className="d-none d-sm-table-cell">{formatDate(rowData.createdUtc)}</td>
      <td className="d-none d-lg-table-cell">{rowData.caseTypeName ?? ''}</td>
      <td>
        <span className={`case-status-indicator ${caseStatusMeta.variantClass}`}>
          <span className="case-status-indicator__dot" />
          <span>{caseStatusMeta.label}</span>
        </span>
        {rowData.reportStatus && (
          <div className="d-sm-none mt-1">
            <ReportStatusBadge rowData={rowData} settings={settings} />
          </div>
        )}
      </td>
      <td className="d-none d-sm-table-cell">
        <ReportStatusBadge rowData={rowData} settings={settings} />
      </td>
      <td className="d-none d-lg-table-cell">{formatDate(rowData.lastUpdatedUtc)}</td>
    </tr>
  )
}
